import {useCallback, useEffect, useRef, useState} from 'react'
import {useTheme} from 'styled-components'

import {CLOUDINARY_CONFIG, UPLOAD_WIDGET_CONFIG, validateCloudinaryConfig} from '../config/cloudinary'

type CloudinaryWidget = {
open: () => void
destroy: () => void
}
type CloudinaryResultInfo = {
secure_url: string
public_id: string
  original_width?: number
  width?: number
  original_height?: number
  height?: number
  coordinates?: {
    custom?: [number, number, number, number][]
    crop?: [number, number, number, number][]
  }
}

type CloudinaryResult = {
  event: string
  info: CloudinaryResultInfo
}

type CloudinaryInstance = {
  createUploadWidget: (
    config: Record<string, unknown>,
    callback: (error: Error | null, result: CloudinaryResult) => void
  ) => CloudinaryWidget
}

type Theme = {
  colors: {
    white?: string
    darkRed?: string
    secondary?: string
    gray?: string
    primary?: string
    bgGray?: string
    border?: string
  }
}

// Singleton pattern for script loading only
let cloudinaryScriptPromise: Promise<CloudinaryInstance> | null = null

const loadCloudinaryScript = (): Promise<CloudinaryInstance> => {
  if (cloudinaryScriptPromise) {
    return cloudinaryScriptPromise
  }

  cloudinaryScriptPromise = new Promise((resolve, reject) => {
    const win = window as unknown as {cloudinary: CloudinaryInstance}
    if (win.cloudinary) {
      resolve(win.cloudinary)
      return
    }

    const script = document.createElement('script')
    script.src = 'https://upload-widget.cloudinary.com/global/all.js'
    script.async = true
    script.onload = () => {
      resolve(win.cloudinary)
    }
    script.onerror = () => {
      reject(new Error('Failed to load Cloudinary script'))
    }
    document.head.appendChild(script)
  })

  return cloudinaryScriptPromise
}

const insertTransformationIntoUrl = (secureUrl: string, transformation: string) => {
  if (!secureUrl) {
    return secureUrl
  }
  const marker = '/upload/'
  const idx = secureUrl.indexOf(marker)
  if (idx === -1) {
    return secureUrl
  }
  const prefix = secureUrl.slice(0, idx + marker.length)
  const suffix = secureUrl.slice(idx + marker.length)
  return `${prefix}${transformation}/${suffix}`
}

const buildCroppedSecureUrl = (info: CloudinaryResultInfo) => {
  if (!info) {
    return null
  }
  const baseUrl = info.secure_url
  const originalWidth = info.original_width || info.width || 0
  const originalHeight = info.original_height || info.height || 0
  const coords = (info.coordinates && (info.coordinates.custom?.[0] || info.coordinates.crop?.[0])) || null
  if (!coords) {
    return baseUrl
  }

  let x: number
  let y: number
  let width: number
  let height: number

  if (Array.isArray(coords) && coords.length >= 4) {
    [x, y, width, height] = coords as [number, number, number, number]
  } else if (
    coords &&
    (Object.hasOwn(coords, 'x') || Object.hasOwn(coords, 'left'))
  ) {
    const c = coords as unknown as { x?: number, left?: number, y?: number, top?: number, width: number, height: number }
    x = c.x ?? c.left ?? 0
    y = c.y ?? c.top ?? 0
    width = c.width
    height = c.height
  } else {
    return baseUrl
  }

  if (![x, y, width, height].every((v) => Number.isFinite(Number(v)))) {
    return baseUrl
  }

  x = Number(x)
  y = Number(y)
  width = Number(width)
  height = Number(height)

  const isPercent = x > 0 && y > 0 && width > 0 && height > 0 && x <= 1 && y <= 1 && width <= 1 && height <= 1
  if (isPercent) {
    x = Math.round(x * originalWidth)
    y = Math.round(y * originalHeight)
    width = Math.round(width * originalWidth)
    height = Math.round(height * originalHeight)
  } else {
    x = Math.round(x)
    y = Math.round(y)
    width = Math.round(width)
    height = Math.round(height)
  }

  const transform = `c_crop,x_${x},y_${y},w_${width},h_${height}`
  return insertTransformationIntoUrl(baseUrl, transform)
}

export const useCloudinaryUpload = (
  onSuccess: (result: { secure_url: string, public_id: string }) => void,
  onError?: (error: any) => void,
  onClose?: () => void
) => {
  const [isReady, setIsReady] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const isInitialized = useRef(false)
  const widgetInstance = useRef<CloudinaryWidget | null>(null)
  const onSuccessRef = useRef(onSuccess)
  const onErrorRef = useRef(onError)
  const onCloseRef = useRef(onClose)
  const theme = useTheme() as unknown as Theme

  // Update refs when callbacks change
  useEffect(() => {
    onSuccessRef.current = onSuccess
    onErrorRef.current = onError
    onCloseRef.current = onClose
  }, [onSuccess, onError, onClose])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (widgetInstance.current) {
        try {
          widgetInstance.current.destroy()
        } catch (e) {
          console.warn('Error destroying widget on unmount:', e)
        }
        widgetInstance.current = null
      }
    }
  }, [])

  const initializeWidget = useCallback(async () => {
    if (isInitialized.current) {
      return
    }

    if (!validateCloudinaryConfig()) {
      console.error('Cloudinary configuration is invalid.')
      if (onErrorRef.current) {
        onErrorRef.current('Cloudinary configuration is invalid')
      }
      return
    }

    try {
      setIsLoading(true)
      const cloudinary = await loadCloudinaryScript()

      // Check again after async load
      if (isInitialized.current) {
        return
      }

      const widget = cloudinary.createUploadWidget(
        {
          ...UPLOAD_WIDGET_CONFIG,
          cloudName: (CLOUDINARY_CONFIG.cloudName as string),
          uploadPreset: (CLOUDINARY_CONFIG.uploadPreset as string),
          styles: {
            palette: {
              window: theme?.colors?.white || '#ffffff',
              error: theme?.colors?.darkRed || '#f44336',
              textDark: theme?.colors?.secondary || '#000000',
              menuIcons: theme?.colors?.gray || '#5a616a',
              action: theme?.colors?.primary || '#0078ff',
              sourceBg: theme?.colors?.bgGray || '#f4f4f4',
              tabIcon: theme?.colors?.primary || '#0078ff',
              complete: '#2ecc71',
              inProgress: theme?.colors?.primary || '#0078ff',
              windowBorder: theme?.colors?.border || '#90a0b3',
              textLight: '#ffffff',
              inactiveTabIcon: theme?.colors?.gray || '#69778a',
              link: theme?.colors?.primary || '#0078ff',
            },
            fonts: {
              default: null
            }
          }
        },
        (error, result) => {
          if (result?.event && result.event !== 'display-changed') {
            console.log('Cloudinary Widget Event:', result.event, result.info || '')
          }

          if (!error && result && result.event === 'success') {
            try {
              const {public_id} = result.info
              const croppedUrl = buildCroppedSecureUrl(result.info) || result.info.secure_url
              if (onSuccessRef.current) {
                onSuccessRef.current({secure_url: croppedUrl, public_id})
              }
            } catch (err) {
              console.error('Error processing upload success:', err)
              if (onErrorRef.current) {
                onErrorRef.current(err instanceof Error ? err : String(err))
              }
            }
          }

          if (result && result.event === 'close') {
            if (onCloseRef.current) {
              onCloseRef.current()
            }
          }

          if (error) {
            console.error('Cloudinary Widget Error:', error)
            if (onErrorRef.current) {
              onErrorRef.current(error)
            }
          }
        }
      )

      widgetInstance.current = widget
      isInitialized.current = true
      setIsReady(true)
    } catch (error) {
      console.error('Failed to initialize Cloudinary widget:', error)
      if (onErrorRef.current) {
        onErrorRef.current('Failed to initialize upload widget')
      }
    } finally {
      setIsLoading(false)
    }
  }, [theme])

  useEffect(() => {
    initializeWidget()
  }, [initializeWidget])

  const openUploadWidget = useCallback(() => {
    if (!validateCloudinaryConfig()) {
      return
    }

    if (!isReady) {
      console.warn('Widget is not ready yet.')
      return
    }

    if (widgetInstance.current) {
      widgetInstance.current.open()
    } else {
      console.error('Widget instance is missing.')
    }
  }, [isReady])

  return {
    openUploadWidget,
    isReady,
    isLoading
  }
}
